import React from 'react';
import '@/App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { LocationProvider } from '@/contexts/LocationContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import AppShell from '@/layouts/AppShell';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import LocatiesHub from '@/pages/LocatiesHub';
import LocationCockpit from '@/pages/LocationCockpit';
import Medewerkers from '@/pages/Medewerkers';
import Vacatures from '@/pages/Vacatures';
import MedewerkerDetail from '@/pages/MedewerkerDetail';
import Availability from '@/pages/Availability';
import Standaardrooster from '@/pages/Standaardrooster';
import Roosterplanner from '@/pages/Roosterplanner';
import Clock from '@/pages/Clock';
import PortalLogin from '@/pages/PortalLogin';
import PublicVacature from '@/pages/PublicVacature';
import PublicVacatures from '@/pages/PublicVacatures';
import PortalRoster from '@/pages/PortalRoster';
import Instellingen from '@/pages/Instellingen';
import AuditLog from '@/pages/AuditLog';
import AIUsage from '@/pages/AIUsage';
import AIHealth from '@/pages/AIHealth';
import UrenOverzicht from '@/pages/UrenOverzicht';
import Verloning from '@/pages/Verloning';
import Verlofaanvragen from '@/pages/Verlofaanvragen';
import Loonperiode from '@/pages/Loonperiode';
import UrenImport from '@/pages/UrenImport';
import ContractWizard from '@/pages/ContractWizard';
import Weekoverzicht from '@/pages/Weekoverzicht';
import Meldingen from '@/pages/Meldingen';
import Probleemmeldingen from '@/pages/Probleemmeldingen';
import Instructiebladen from '@/pages/Instructiebladen';
import MijnAccount from '@/pages/MijnAccount';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Placeholder from '@/pages/Placeholder';

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
      <AuthProvider>
      <BrowserRouter>
        <LocationProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portaal/login" element={<PortalLogin />} />
            <Route path="/werken-bij" element={<PublicVacatures />} />
            <Route path="/werken-bij/:id" element={<PublicVacature />} />
            <Route path="/portaal/rooster" element={<PortalRoster />} />
            <Route path="/portaal" element={<Navigate to="/portaal/rooster" replace />} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
            <Route path="/locaties" element={<Shell><LocatiesHub /></Shell>} />
            <Route path="/locaties/:slug" element={<Shell><LocationCockpit /></Shell>} />
            <Route path="/medewerkers" element={<Shell><Medewerkers /></Shell>} />
            <Route path="/vacatures" element={<Shell><Vacatures /></Shell>} />
            <Route path="/medewerkers/:id" element={<Shell><MedewerkerDetail /></Shell>} />
            <Route path="/beschikbaarheid" element={<Shell><Availability /></Shell>} />
            <Route path="/standaardrooster" element={<Shell><Standaardrooster /></Shell>} />
            <Route path="/rooster" element={<Shell><Roosterplanner /></Shell>} />
            <Route path="/uren-overzicht" element={<Shell><UrenOverzicht /></Shell>} />
            {/* Legacy deep-link → canonieke Uren-review preset (declaratief, geen effect-race). */}
            <Route path="/goedkeuringen" element={<Navigate to="/uren-overzicht?filter=review" replace />} />
            <Route path="/verloning" element={<Shell><Verloning /></Shell>} />
            <Route path="/verlofaanvragen" element={<Shell><Verlofaanvragen /></Shell>} />
            <Route
              path="/loonperiode"
              element={
                <ProtectedRoute roles={['owner', 'admin', 'multi_location', 'single_location']}>
                  <AppShell><Loonperiode /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/contracten" element={<Shell><ContractWizard /></Shell>} />
            <Route
              path="/uren-import"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <AppShell><UrenImport /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/weekoverzicht" element={<Shell><Weekoverzicht /></Shell>} />
            <Route path="/meldingen" element={<Shell><Meldingen /></Shell>} />
            <Route
              path="/probleemmeldingen"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <AppShell><Probleemmeldingen /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/clock" element={<ProtectedRoute><Clock /></ProtectedRoute>} />
            <Route path="/inklokken" element={<ProtectedRoute><Clock /></ProtectedRoute>} />
            <Route
              path="/instellingen"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <AppShell><Instellingen /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/audit-log" element={<Shell><AuditLog /></Shell>} />
            <Route path="/mijn-account" element={<Shell><MijnAccount /></Shell>} />
            <Route
              path="/instructiebladen"
              element={
                <ProtectedRoute roles={['owner', 'admin', 'multi_location', 'single_location']}>
                  <AppShell><Instructiebladen /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-usage"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <AppShell><AIUsage /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-health"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <AppShell><AIHealth /></AppShell>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </LocationProvider>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
